import React, { Fragment } from "react";
import { filter } from "lodash";
import { IconColor, IconName } from "@/shared/ui/Icon";

import {
  DropdownMenu as DropdownMenuBase,
  DropdownMenuProps as DropdownMenuBaseProps,
  DropdownMenuContent,
  DropdownMenuContentProps,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/DropDown";
import { IconButton } from "@/shared/ui/IconButton";
import { Column } from "@/shared/ui/Blocks";

export interface DropdownMenuItem {
  label: string;
  icon?: IconName;
  iconColor?: IconColor;
  onClick?: (e: React.MouseEvent) => void;
  options?: DropdownMenuItem[];
  selected?: boolean;
  hidden?: boolean;
  renderItem?: (item: DropdownMenuItem) => React.ReactNode;
}

export interface DropdownMenuProps
  extends Omit<DropdownMenuContentProps, "children"> {
  items: DropdownMenuItem[];
  trigger: React.ReactNode;
  separatorIndexes?: number[];
  HeaderComponent?: React.ReactNode;
  FooterComponent?: React.ReactNode;
  rootProps?: DropdownMenuBaseProps;
}

function MenuItem(props: DropdownMenuItem) {
  const { label, icon, onClick, renderItem, iconColor } = props;

  if (renderItem) {
    return renderItem(props);
  }

  const handleClick = (e: React.MouseEvent) => {
    onClick?.(e);
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <DropdownMenuItem
      onClick={handleClick}
      className="cursor-pointer text-bold"
    >
      <IconButton
        size="sm"
        color={iconColor || "black"}
        variant="ghost"
        as="span"
        icon={icon as IconName}
        className="w-full justify-start"
        label={label}
      />
    </DropdownMenuItem>
  );
}

function DropdownMenu(props: DropdownMenuProps) {
  const {
    items,
    trigger,
    separatorIndexes,
    HeaderComponent,
    FooterComponent,
    rootProps = {},
    ...rest
  } = props;

  return (
    <DropdownMenuBase {...rootProps}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        className="border-grey-300 bg-white border-solid"
        {...rest}
      >
        <Column className="gap-2">
          {HeaderComponent}
          {filter(items, (i) => !i.hidden).map((item, index) => {
            const { label } = item;
            const hasSeparator = separatorIndexes?.includes(index);
            return (
              <Fragment key={`${label}-${index}`}>
                {hasSeparator && <DropdownMenuSeparator />}
                <MenuItem {...item} />
              </Fragment>
            );
          })}
          {FooterComponent}
        </Column>
      </DropdownMenuContent>
    </DropdownMenuBase>
  );
}

export { DropdownMenu };
